import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, Phone, Globe } from 'lucide-react';
import logo from '../logo.jpg';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <img src={logo} alt="SupaKid Logo" className="w-8 h-8" />
              <span className="text-xl font-bold text-gray-800">SupaKid</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-8 py-12 text-white">
            <div className="flex items-center space-x-3 mb-4">
              <FileText className="w-8 h-8" />
              <h1 className="text-3xl font-bold">Terms of Service</h1>
            </div>
            <p className="text-blue-100 text-lg">
              Please read these terms carefully before using Supakid
            </p>
            <p className="text-blue-200 text-sm mt-2">
              Last Updated: January 2026
            </p>
          </div>

          {/* Content */}
          <div className="px-8 py-8">
            <div className="prose prose-lg max-w-none">
              
              {/* Acceptance of Terms */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-600 leading-relaxed">
                  Welcome to Supakid ("we," "our," or "us"). By downloading, installing, accessing, or using the Supakid mobile application (the "App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the App.
                </p>
              </section>

              {/* Description of Service */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Description of Service</h2>
                <p className="text-gray-600 leading-relaxed mb-3">
                  Supakid is a mobile application that provides curated, age-appropriate video content for families. The App allows parents to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Create and manage child profiles</li>
                  <li>Access curated video content filtered by age groups</li>
                  <li>Set screen time limits and parental controls</li>
                  <li>Secure profiles with PIN protection</li>
                </ul>
              </section>

              {/* Account Registration */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Account Registration</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">3.1 Account Creation</h3>
                <p className="text-gray-600 leading-relaxed mb-3">To use Supakid, you must create an account by providing:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>A valid email address</li>
                  <li>A secure password</li>
                  <li>Your name</li>
                  <li>An admin PIN (4 digits) for accessing parental settings</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">3.2 Account Security</h3>
                <p className="text-gray-600 leading-relaxed mb-3">You are responsible for:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Keeping your admin PIN secure</li>
                  <li>Notifying us immediately of any unauthorized use</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">3.3 Email Verification</h3>
                <p className="text-gray-600 leading-relaxed">
                  You must verify your email address before your account is fully activated. We will send a confirmation code to your email address during registration.
                </p>
              </section>

              {/* Subscription and Payment */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Subscription and Payment</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">4.1 Subscription Plans</h3>
                <p className="text-gray-600 leading-relaxed mb-3">Supakid offers the following subscription plans:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Monthly Plan:</strong> $10.00 per month</li>
                  <li><strong>Annual Plan:</strong> $60.00 per year</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">4.2 Auto-Renewal</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current billing period. You can manage your subscription and cancel at any time through your Apple ID account settings.
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">4.3 Payment Processing</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  All payments are processed through Apple's App Store. By purchasing a subscription, you agree to Apple's payment terms and conditions.
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">4.4 Refunds</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Refund requests are subject to Apple's refund policy. We do not provide direct refunds. Please contact Apple Support for refund requests.
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">4.5 Subscription Cancellation</h3>
                <p className="text-gray-600 leading-relaxed">
                  You may cancel your subscription at any time through your Apple ID account settings. Cancellation will take effect at the end of your current billing period. You will continue to have access to premium features until the end of your paid period.
                </p>
              </section>

              {/* User Content and Profiles */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">5. User Content and Profiles</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">5.1 Child Profiles</h3>
                <p className="text-gray-600 leading-relaxed mb-3">You may create child profiles within the App. You are responsible for:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>The accuracy of information provided for child profiles</li>
                  <li>Maintaining the security of profile PINs</li>
                  <li>Ensuring appropriate use of profiles by children</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">5.2 Content Responsibility</h3>
                <p className="text-gray-600 leading-relaxed mb-3">You are solely responsible for:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>The content and information you provide</li>
                  <li>How your account and profiles are used</li>
                  <li>Any consequences of sharing your account credentials</li>
                </ul>
              </section>

              {/* Acceptable Use */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Acceptable Use</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">6.1 Permitted Use</h3>
                <p className="text-gray-600 leading-relaxed mb-3">You may use the App only for lawful purposes and in accordance with these Terms. You agree not to:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Use the App in any way that violates any applicable law or regulation</li>
                  <li>Attempt to gain unauthorized access to the App or related systems</li>
                  <li>Interfere with or disrupt the App or servers</li>
                  <li>Use automated systems to access the App without permission</li>
                  <li>Share your account credentials with others</li>
                  <li>Create multiple accounts to circumvent subscription requirements</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">6.2 Prohibited Activities</h3>
                <p className="text-gray-600 leading-relaxed mb-3">You agree not to:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Reverse engineer, decompile, or disassemble the App</li>
                  <li>Remove or alter any copyright, trademark, or other proprietary notices</li>
                  <li>Use the App to transmit any harmful code or malware</li>
                  <li>Impersonate any person or entity</li>
                  <li>Collect or harvest information about other users</li>
                </ul>
              </section>

              {/* Third-Party Content */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Third-Party Content</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">7.1 YouTube Content</h3>
                <p className="text-gray-600 leading-relaxed mb-3">The App provides access to video content through YouTube's API. All video content is subject to:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>YouTube's Terms of Service:</strong> <a href="https://www.youtube.com/static?template=terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">https://www.youtube.com/static?template=terms</a></li>
                  <li>YouTube's Community Guidelines</li>
                  <li>Content creator rights and licenses</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mb-3">We do not own, control, or endorse the video content available through the App. We are not responsible for:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>The accuracy, legality, or appropriateness of third-party content</li>
                  <li>Any claims or disputes related to third-party content</li>
                  <li>Changes to content availability</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">7.2 Content Curation</h3>
                <p className="text-gray-600 leading-relaxed">
                  While we curate content to be age-appropriate, we cannot guarantee that all content will be suitable for all users. Parents are responsible for monitoring their children's use of the App.
                </p>
              </section>

              {/* Intellectual Property */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Intellectual Property</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">8.1 Our Rights</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  The App, including its design, features, and functionality, is owned by Supakid LLC and protected by copyright, trademark, and other intellectual property laws.
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">8.2 Your Rights</h3>
                <p className="text-gray-600 leading-relaxed">
                  You retain ownership of any content you create within the App (such as child profiles). By using the App, you grant us a license to use, store, and process your content as necessary to provide the service.
                </p>
              </section>

              {/* Privacy */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Privacy</h2>
                <p className="text-gray-600 leading-relaxed">
                  Your privacy is important to us. Please review our <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800">Privacy Policy</Link>, which explains how we collect, use, and protect your information. By using the App, you consent to the collection and use of information as described in our Privacy Policy.
                </p>
              </section>

              {/* Account Termination */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Account Termination</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">10.1 Termination by You</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  You may delete your account at any time through the App's settings. Account deletion will permanently remove all your data, including child profiles and preferences.
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">10.2 Termination by Us</h3>
                <p className="text-gray-600 leading-relaxed mb-3">We reserve the right to suspend or terminate your account if you:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Violate these Terms</li>
                  <li>Engage in fraudulent or illegal activity</li>
                  <li>Misuse the App or its features</li>
                  <li>Fail to pay subscription fees</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">10.3 Effect of Termination</h3>
                <p className="text-gray-600 leading-relaxed mb-3">Upon termination:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Your access to the App will be immediately revoked</li>
                  <li>All data associated with your account will be deleted</li>
                  <li>You will not be entitled to any refund for unused subscription time</li>
                </ul>
              </section>

              {/* Disclaimers */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">11. Disclaimers</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">11.1 Service Availability</h3>
                <p className="text-gray-600 leading-relaxed mb-3">We strive to provide continuous service, but we do not guarantee that the App will be available at all times. The App may be unavailable due to:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Maintenance or updates</li>
                  <li>Technical issues</li>
                  <li>Third-party service outages (e.g., YouTube API)</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">11.2 Content Accuracy</h3>
                <p className="text-gray-600 leading-relaxed mb-3">While we curate content for age-appropriateness, we do not guarantee:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>The accuracy or completeness of video content</li>
                  <li>That all content will be suitable for all users</li>
                  <li>Continuous availability of specific videos</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">11.3 No Warranties</h3>
                <p className="text-gray-600 leading-relaxed font-semibold">
                  THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                </p>
              </section>

              {/* Limitation of Liability */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">12. Limitation of Liability</h2>
                <p className="text-gray-600 leading-relaxed font-semibold">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATING TO YOUR USE OF THE APP.
                </p>
              </section>

              {/* Indemnification */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">13. Indemnification</h2>
                <p className="text-gray-600 leading-relaxed mb-3">
                  You agree to indemnify and hold harmless Supakid LLC, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising out of:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Your use of the App</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any rights of another party</li>
                </ul>
              </section>

              {/* Changes to Terms */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">14. Changes to Terms</h2>
                <p className="text-gray-600 leading-relaxed mb-3">
                  We reserve the right to modify these Terms at any time. We will notify you of material changes by:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-3">
                  <li>Posting the updated Terms in the App</li>
                  <li>Sending an email to your registered email address</li>
                  <li>Displaying a notice in the App</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Your continued use of the App after changes become effective constitutes acceptance of the modified Terms.
                </p>
              </section>

              {/* Governing Law */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">15. Governing Law</h2>
                <p className="text-gray-600 leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
                </p>
              </section>

              {/* Dispute Resolution */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">16. Dispute Resolution</h2>
                <p className="text-gray-600 leading-relaxed">
                  Any disputes arising out of or relating to these Terms or the App shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
                </p>
              </section>

              {/* Contact Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">17. Contact Information</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If you have questions about these Terms, please contact us:
                </p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-gray-800 mb-2">Supakid LLC</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-semibold text-gray-800">Email</p>
                        <a href="mailto:support@supakidapp.com" className="text-blue-600 hover:text-blue-800">
                          support@supakidapp.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-semibold text-gray-800">Phone</p>
                        <a href="tel:+17653511136" className="text-blue-600 hover:text-blue-800">
                          (765) 351-1136
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Globe className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-semibold text-gray-800">Website</p>
                        <a href="https://www.supakidapp.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          https://www.supakidapp.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Severability */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">18. Severability</h2>
                <p className="text-gray-600 leading-relaxed">
                  If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
                </p>
              </section>

              {/* Entire Agreement */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">19. Entire Agreement</h2>
                <p className="text-gray-600 leading-relaxed">
                  These Terms, together with our <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800">Privacy Policy</Link>, constitute the entire agreement between you and Supakid LLC regarding the use of the App.
                </p>
              </section>

              {/* Acknowledgment */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">20. Acknowledgment</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  By using the App, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
                  <p className="text-blue-800 font-semibold text-center">
                    By downloading and using Supakid, you agree to these Terms of Service.
                  </p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 mt-12 border-t border-gray-200">
        <div className="container mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-3">
            <img src={logo} alt="SupaKid Logo" className="w-6 h-6" />
            <span className="text-lg font-bold text-gray-800">SupaKid</span>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Questions? Contact us at{" "}
            <a
              href="mailto:support@supakidapp.com"
              className="text-blue-600 hover:text-blue-800"
            >
              support@supakidapp.com
            </a>
          </p>
          <p className="text-xs text-gray-500">© 2024 SupaKid. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
